import { makeAutoObservable, runInAction } from "mobx";
import { TYPE, rootContainer } from "../../services/container/root";
import PhysicalStoreService, { IPhysicalStoreStaffPreselection, ShopListItem } from "../../services/api/physical-store-service";
import { IStoreLocation } from "../../models/User";
import { IIframeService } from "../../services/iframe-service/iframe-service";

class Shop {
    constructor(private _shop: ShopListItem) {}
    public get name() { return this._shop.store_name ?? this._shop.name; }
    public get id() { return this._shop.id; }
}

class ChooseStoreLocationStore {
    private _locationsByStore: { [store: string]: IStoreLocation[] } = {};
    private _shops: Shop[] = [];
    private _selectedStoreId?: number;
    private _selectedStoreLocationId?: string;
    private _isSaving: boolean = false;
    private _isLoadingStores: boolean = false;
    private _isLoadingLocations: boolean = false;
    private _rememberSelection: boolean = true;
    private _preselect?: IPhysicalStoreStaffPreselection;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): PhysicalStoreService {
        return rootContainer.get<PhysicalStoreService>(TYPE.PhysicalStoreService);
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    public get locations() { return this._locationsByStore[this._selectedStoreId ?? '---']; }
    public get shops() { return this._shops; }
    public get selectedStoreId() { return this._selectedStoreId; }
    public get physicalStoreLocationId() { return this._selectedStoreLocationId; }
    public get isSaving() { return this._isSaving; }
    public get isLoadingStores() { return this._isLoadingStores; }
    public get isLoadingLocations() { return this._isLoadingLocations; }
    public get rememberSelection() { return this._rememberSelection; }

    public async load() {
        this._isLoadingStores = true;

        try {
            const [preselection, storeResponse] = await Promise.all([
                this.service.getPhysicalStoreStaffPreselection(),
                this.service.getStoresList()
            ]);

            runInAction(() => {
                this._preselect = preselection;
                
                /** the selected store id should be the 1st - priority current logged-on store, 2nd - from DB presets */
                /** auto select store if it is the only option */
                let selectedStoreId = storeResponse.current_store_id ?? preselection.store_id;
                if (storeResponse.stores.length === 1) {
                    selectedStoreId = storeResponse.stores[0].id;
                }
                /** make sure selectedStoreId is one of stores from the list, 
                 * user could be disabled in selected store from presets and in this case we should not set the selected */
                if (storeResponse.stores.some(s => s.id === selectedStoreId)) {
                    this.setSelectedStoreId(selectedStoreId);
                }

                this._shops = storeResponse.stores.map(s => new Shop(s));
                this._isLoadingStores = false;
            });
        } catch (error) {
            runInAction(() => {
                this._isLoadingStores = false;
            });
            console.error(error);
        }
    }

    private async _loadLocations(storeId: number): Promise<IStoreLocation[]> {
        try {
            if (this._locationsByStore[storeId]) {
                return this._locationsByStore[storeId];
            }

            this._isLoadingLocations = true;
            const locations = await this.service.getLocations(storeId);
            runInAction(() => {
                this._locationsByStore[storeId] = locations;
                this._isLoadingLocations = false;
            });
            return locations;
        }
        catch (e) {
            runInAction(() => {
                this._isLoadingLocations = false;
            });
        }
        return [];
    }

    public setSelectedStoreId(value: number) {
        if (value === undefined || value === null) {
            return;
        }

        this._selectedStoreId = Number(value);
        this._selectedStoreLocationId = undefined;
        this._loadLocations(value);
        this._handlePreselect();
    }

    private _handlePreselect() {
        if (this._selectedStoreId === this._preselect?.store_id) {
            this._selectedStoreLocationId = this._preselect?.location_id;
        }
    }

    public setLocationId(value: string) {
        this._selectedStoreLocationId = value;
    }



    public setRememberSelection(value: boolean) {
        this._rememberSelection = value;
    }

    private _getLocationName() {
        const locations = this._locationsByStore[this._selectedStoreId as number];
        return locations.find(location => location.location_id === this._selectedStoreLocationId)?.name ?? '';
    }

    public async saveLocationId() {
        if (!this._selectedStoreId || !this._selectedStoreLocationId || this._isSaving || this._isLoadingStores) {
            return;
        }
        this._isSaving = true;

        try {
            await this.service.setPhysicalStoreStaffLocationId(
                this._selectedStoreId,
                this._selectedStoreLocationId,
                this._getLocationName(),
                this._rememberSelection
            );
            runInAction(() => {
                this._isSaving = false;
            });

            this.iframeService.redirectParentFrame('/dashboard/returns');
        }
        catch (e) {
            runInAction(() => {
                this._isSaving = false;
            });
        }

    }
}

export default new ChooseStoreLocationStore();
